(() => {
  if (customElements.get('rudra-ai')) return;

  class RudraAI extends HTMLElement {
    constructor() {
      super();
      this.history = [];
      this.busy = false;
      this.lastFocus = null;
      this.storageKey = `rudra-ai-history-${this.dataset.sectionId || 'global'}`;
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-rudra-panel]');
      this.launcher = this.querySelector('[data-rudra-open]');
      this.messages = this.querySelector('[data-rudra-messages]');
      this.typing = this.querySelector('[data-rudra-typing]');
      this.quickPrompts = this.querySelector('[data-rudra-quick-prompts]');
      this.forms = [...this.querySelectorAll('[data-rudra-form]')];
      this.context = this.readJSON('[data-rudra-context]', {});
      this.prompts = this.readJSON('[data-rudra-prompts]', []);
      this.endpoint = (this.dataset.apiEndpoint || '').trim();
      this.maxLength = Number(this.dataset.maxMessageLength || 1200);
      this.requestTimeout = Number(this.dataset.requestTimeout || 20000);
      this.persist = this.dataset.persistChat === 'true';

      this.bindEvents();
      this.restoreHistory();
      if (!this.history.length) {
        this.addMessage('assistant', this.dataset.welcomeMessage || 'Namaste. How can I help?');
      } else {
        this.renderHistory();
      }
    }

    readJSON(selector, fallback) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '');
      } catch (error) {
        return fallback;
      }
    }

    bindEvents() {
      this.launcher?.addEventListener('click', () => this.openPanel());
      this.querySelector('[data-rudra-close]')?.addEventListener('click', () => this.closePanel());
      this.querySelector('[data-rudra-clear]')?.addEventListener('click', () => this.clearConversation());

      this.forms.forEach((form) => {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          const input = form.elements.message;
          const value = input.value.trim();
          if (!value || this.busy) return;
          input.value = '';
          this.ask(value, form.dataset.rudraSurface === 'landing');
        });
      });

      this.querySelectorAll('[data-rudra-question]').forEach((button) => {
        button.addEventListener('click', () => {
          const question = button.dataset.rudraQuestion?.trim();
          if (question && !this.busy) this.ask(question, true);
        });
      });

      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
    }

    handleKeydown = (event) => {
      if (event.key === 'Escape' && this.panel && !this.panel.hidden) this.closePanel();
    };

    handleSectionUnload = (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) this.remove();
    };

    openPanel() {
      if (!this.panel) return;
      this.lastFocus = document.activeElement;
      this.panel.hidden = false;
      this.launcher?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.panel.querySelector('input')?.focus({ preventScroll: true }));
    }

    closePanel() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.launcher?.setAttribute('aria-expanded', 'false');
      if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus({ preventScroll: true });
    }

    clearConversation() {
      this.history = [];
      this.messages.replaceChildren();
      this.saveHistory();
      this.quickPrompts?.removeAttribute('hidden');
      this.addMessage('assistant', this.dataset.welcomeMessage || 'Namaste. How can I help?');
    }

    async ask(rawMessage, open) {
      if (open && !this.launcher) {
        const globalAssistant = [...document.querySelectorAll('rudra-ai')]
          .find((assistant) => assistant !== this && assistant.launcher && typeof assistant.ask === 'function');
        if (globalAssistant) {
          if (this.prompts.length) globalAssistant.prompts = this.prompts;
          if (this.endpoint) globalAssistant.endpoint = this.endpoint;
          return globalAssistant.ask(rawMessage, true);
        }
      }

      const message = rawMessage.slice(0, this.maxLength).trim();
      if (!message || this.busy) return;
      if (open) this.openPanel();

      this.setBusy(true);
      this.quickPrompts?.setAttribute('hidden', '');
      this.addMessage('user', message);

      let matches = [];
      try {
        matches = await this.searchStore(message);
      } catch (error) {
        matches = [];
      }

      try {
        const response = this.endpoint
          ? await this.requestAI(message, matches)
          : this.localAnswer(message, matches);
        this.addMessage('assistant', response.answer, {
          linkLabel: response.link_label,
          linkUrl: response.link_url,
          results: response.results || matches
        });
      } catch (error) {
        const fallback = this.localAnswer(message, matches);
        const notice = this.dataset.errorMessage || 'The live AI service is unavailable.';
        this.addMessage('assistant', `${notice}\n\n${fallback.answer}`, {
          linkLabel: fallback.link_label,
          linkUrl: fallback.link_url,
          results: fallback.results || matches
        });
      } finally {
        this.setBusy(false);
      }
    }

    async requestAI(message, matches) {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), this.requestTimeout);
      const safeHistory = this.history.slice(-10).map(({ role, content }) => ({ role, content }));

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Rudra-AI-Client': 'shopify-theme-v1'
          },
          body: JSON.stringify({
            message,
            history: safeHistory,
            page_context: this.context,
            site_matches: matches,
            client: { locale: this.context.locale, currency: this.context.currency }
          }),
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`AI request failed with ${response.status}`);
        const payload = await response.json();
        if (!payload || typeof payload.answer !== 'string' || !payload.answer.trim()) {
          throw new Error('AI response is missing an answer');
        }

        return {
          answer: payload.answer.trim(),
          link_label: payload.link_label || '',
          link_url: this.safeUrl(payload.link_url),
          results: Array.isArray(payload.results) ? payload.results.slice(0, 4) : matches
        };
      } finally {
        window.clearTimeout(timer);
      }
    }

    localAnswer(message, matches) {
      const normalized = this.normalize(message);
      let bestPrompt = null;
      let bestScore = 0;

      this.prompts.forEach((prompt) => {
        const terms = this.normalize(`${prompt.question || ''} ${prompt.keywords || ''}`).split(' ').filter(Boolean);
        const score = terms.reduce((total, term) => total + (normalized.includes(term) ? 1 : 0), 0);
        if (score > bestScore) {
          bestScore = score;
          bestPrompt = prompt;
        }
      });

      if (bestPrompt && bestScore > 0) {
        return {
          answer: bestPrompt.answer || this.dataset.fallbackMessage,
          link_label: bestPrompt.link_label || '',
          link_url: this.safeUrl(bestPrompt.link_url),
          results: matches
        };
      }

      if (matches.length) {
        return {
          answer: `I found ${matches.length === 1 ? 'this relevant page' : 'these relevant pages'} in ${this.context.shop_name || 'the store'}. Open a result below for the latest product, collection or article details.`,
          results: matches
        };
      }

      const pageHint = this.context.page_title ? ` You are currently viewing “${this.context.page_title}”.` : '';
      return {
        answer: `${this.dataset.fallbackMessage || 'I could not find a confident answer yet.'}${pageHint}`,
        results: []
      };
    }

    normalize(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9₹]+/g, ' ')
        .trim();
    }

    async searchStore(query) {
      const searchUrl = this.dataset.searchUrl;
      if (!searchUrl || query.length < 2) return [];

      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('q', query.slice(0, 120));
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      const resources = data?.resources?.results || {};
      const matches = [];

      Object.entries(resources).forEach(([type, entries]) => {
        if (!Array.isArray(entries)) return;
        entries.forEach((entry) => {
          const itemUrl = this.safeUrl(entry.url);
          if (!itemUrl || !entry.title) return;
          matches.push({
            type: type.replace(/s$/, ''),
            title: String(entry.title).slice(0, 180),
            url: itemUrl,
            image: this.safeImage(entry.featured_image?.url || entry.image || ''),
            price: entry.price || ''
          });
        });
      });

      return matches.slice(0, 4);
    }

    safeUrl(value) {
      if (!value) return '';
      try {
        const url = new URL(String(value), window.location.origin);
        return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : '';
      } catch (error) {
        return '';
      }
    }

    safeImage(value) {
      if (!value) return '';
      try {
        const url = new URL(String(value), window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
      } catch (error) {
        return '';
      }
    }

    addMessage(role, content, options = {}) {
      const cleanContent = String(content || '').trim();
      if (!cleanContent) return;

      const record = {
        role,
        content: cleanContent,
        time: new Date().toISOString(),
        linkLabel: options.linkLabel || '',
        linkUrl: this.safeUrl(options.linkUrl),
        results: Array.isArray(options.results) ? options.results.slice(0, 4) : []
      };
      this.history.push(record);
      this.history = this.history.slice(-24);
      this.renderMessage(record);
      this.saveHistory();
    }

    renderHistory() {
      this.messages.replaceChildren();
      this.history.forEach((message) => this.renderMessage(message));
    }

    renderMessage(message) {
      const item = document.createElement('div');
      item.className = `rudra-ai__message rudra-ai__message--${message.role === 'user' ? 'user' : 'assistant'}`;

      const bubble = document.createElement('div');
      bubble.className = 'rudra-ai__bubble';
      bubble.textContent = message.content;
      item.append(bubble);

      if (message.linkUrl) {
        const link = document.createElement('a');
        link.className = 'rudra-ai__message-link';
        link.href = message.linkUrl;
        link.textContent = message.linkLabel || 'Learn more';
        item.append(link);
      }

      (message.results || []).forEach((result) => {
        const resultUrl = this.safeUrl(result.url);
        if (!resultUrl || !result.title) return;
        const link = document.createElement('a');
        link.className = 'rudra-ai__result-card';
        link.href = resultUrl;

        if (result.image) {
          const image = document.createElement('img');
          image.src = this.safeImage(result.image);
          image.alt = '';
          image.loading = 'lazy';
          link.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.setAttribute('aria-hidden', 'true');
          placeholder.textContent = '✦';
          link.append(placeholder);
        }

        const text = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = String(result.title).slice(0, 180);
        const meta = document.createElement('small');
        meta.textContent = [result.type, result.price].filter(Boolean).join(' · ');
        text.append(title, meta);
        link.append(text);
        item.append(link);
      });

      const time = document.createElement('span');
      time.className = 'rudra-ai__message-time';
      time.textContent = this.formatTime(message.time);
      item.append(time);
      this.messages.append(item);
      this.messages.scrollTop = this.messages.scrollHeight;
    }

    formatTime(value) {
      try {
        return new Intl.DateTimeFormat(this.context.locale || undefined, {
          hour: '2-digit',
          minute: '2-digit'
        }).format(new Date(value));
      } catch (error) {
        return '';
      }
    }

    setBusy(state) {
      this.busy = state;
      if (this.typing) this.typing.hidden = !state;
      this.forms.forEach((form) => {
        const button = form.querySelector('button[type="submit"]');
        if (button) button.disabled = state;
      });
      if (state) this.messages.scrollTop = this.messages.scrollHeight;
    }

    saveHistory() {
      if (!this.persist) return;
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
      } catch (error) {
        // Storage may be disabled; chat continues without persistence.
      }
    }

    restoreHistory() {
      if (!this.persist) return;
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        if (Array.isArray(stored)) {
          this.history = stored
            .filter((message) => ['user', 'assistant'].includes(message.role) && typeof message.content === 'string')
            .slice(-24);
        }
      } catch (error) {
        this.history = [];
      }
    }
  }

  customElements.define('rudra-ai', RudraAI);
})();
